// src/pages/Assignments.tsx
// ✅ REDESIGN 2024-12-22: Streamlined officer-centric equipment management
// Removed: Available Items tab (use Inventory), redundant stats
// Focused: Who has what equipment, returns, transfers, history

import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Search, 
  RefreshCw, 
  Package, 
  Calendar,
  ArrowLeftRight,
  RotateCcw,
  History,
  ChevronDown,
  ChevronRight,
  Filter,
  Download,
  User,
  Clock,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import { assignmentAPI } from '../services/assignmentAPI';
import { userAPI } from '../services/userAPI';
import { Assignment } from '../types/assignment';
import { User as UserType } from '../types/user';
import { ReturnItemModal } from '../components/inventory/ReturnItemModal';
import TransferItemModal from '../components/assignments/TransferItemModal';
import { logger } from '../utils/logger';

const assignmentLogger = logger.createServiceLogger('Assignments');

interface AssignedOfficer {
  officerId: string;
  officerName: string;
  badgeNumber: string;
  rank?: string;
  assignments: Assignment[];
}

interface ActivityItem {
  id: string;
  date: string;
  time: string;
  officer: string;
  item: string;
  action: 'Assigned' | 'Returned' | 'Transferred';
  performedBy?: string;
  notes?: string;
}

type TabType = 'current' | 'history';

const Assignments: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('current');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOfficerFilter, setSelectedOfficerFilter] = useState('');
  const [dateRangeFilter, setDateRangeFilter] = useState('30');
  const [actionFilter, setActionFilter] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [users, setUsers] = useState<UserType[]>([]);
  const [recentActivity, setRecentActivity] = useState<ActivityItem[]>([]);
  const [expandedOfficers, setExpandedOfficers] = useState<Set<string>>(new Set());

  // Modal states
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);

  // Stats derived from data
  const totalAssigned = assignments.filter(a => a.status === 'Active').length;
  const officersWithEquipment = new Set(assignments.filter(a => a.status === 'Active').map(a => a.assignedToUserId)).size;

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      await Promise.all([
        loadAssignments(),
        loadUsers(),
        loadRecentActivity()
      ]);
      setLastRefresh(new Date());
      assignmentLogger.info('Assignment data loaded successfully');
    } catch (err) {
      assignmentLogger.error('Failed to load assignment data', undefined, err as Error);
      setError('Failed to load assignment data');
    } finally {
      setLoading(false);
    }
  };

  const loadAssignments = async () => {
    const response = await assignmentAPI.getAssignments({ status: 'Active' });
    if (response.success && response.data) {
      setAssignments(response.data.assignments);
    }
  };

  const loadUsers = async () => {
    const response = await userAPI.getUsers();
    const activeUsers = response.users.filter((user: UserType) => 
      user.employmentStatus === 'Active'
    );
    setUsers(activeUsers);
  };

  const loadRecentActivity = async () => {
    const response = await assignmentAPI.getAssignments({ limit: 50 });
    if (response.success && response.data) {
      const activities: ActivityItem[] = response.data.assignments
        .sort((a: Assignment, b: Assignment) => 
          new Date(b.updatedAt || b.assignmentDate).getTime() - 
          new Date(a.updatedAt || a.assignmentDate).getTime()
        )
        .slice(0, 20)
        .map((assignment: Assignment) => ({
          id: assignment.assignmentId,
          date: new Date(assignment.assignmentDate).toLocaleDateString(),
          time: new Date(assignment.assignmentDate).toLocaleTimeString([], { 
            hour: '2-digit', 
            minute: '2-digit' 
          }),
          officer: assignment.assignedToUserName || 'Unknown Officer',
          item: assignment.itemSerialNumber 
            ? `${assignment.itemName} (${assignment.itemSerialNumber})`
            : assignment.itemName,
          action: assignment.status === 'Active' ? 'Assigned' : 'Returned',
          performedBy: assignment.assignedByUserName
        }));
      setRecentActivity(activities);
    }
  };

  // Group assignments by officer
  const assignedByOfficer: AssignedOfficer[] = React.useMemo(() => {
    const grouped = assignments
      .filter(a => a.status === 'Active')
      .reduce((acc: Record<string, AssignedOfficer>, assignment) => {
        const officerId = assignment.assignedToUserId;
        if (!acc[officerId]) {
          const user = users.find(u => u.userId === officerId);
          acc[officerId] = {
            officerId,
            officerName: assignment.assignedToUserName || 'Unknown Officer',
            badgeNumber: assignment.assignedToUserBadge || '',
            rank: user?.rank,
            assignments: []
          };
        }
        acc[officerId].assignments.push(assignment);
        return acc;
      }, {});

    return Object.values(grouped).sort((a, b) => 
      a.officerName.localeCompare(b.officerName)
    );
  }, [assignments, users]);

  // Filter officers based on search
  const filteredOfficers = assignedByOfficer.filter(officer => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      officer.officerName.toLowerCase().includes(search) ||
      officer.badgeNumber.toLowerCase().includes(search) ||
      officer.assignments.some(a => 
        a.itemName.toLowerCase().includes(search) ||
        (a.itemSerialNumber?.toLowerCase() || '').includes(search)
      )
    );
  });

  // Filter activity based on filters
  const filteredActivity = recentActivity.filter(activity => {
    const matchesOfficer = !selectedOfficerFilter || 
      activity.officer.toLowerCase().includes(selectedOfficerFilter.toLowerCase());
    const matchesAction = !actionFilter || activity.action === actionFilter;
    return matchesOfficer && matchesAction;
  });

  const toggleOfficerExpansion = (odId: string) => {
    const newExpanded = new Set(expandedOfficers);
    if (newExpanded.has(odId)) {
      newExpanded.delete(odId);
    } else {
      newExpanded.add(odId);
    }
    setExpandedOfficers(newExpanded);
  };

  const expandAll = () => {
    setExpandedOfficers(new Set(filteredOfficers.map(o => o.officerId)));
  };

  const collapseAll = () => {
    setExpandedOfficers(new Set());
  };

  const handleReturn = (assignment: Assignment) => {
    setSelectedAssignment(assignment);
    setShowReturnModal(true);
  };

  const handleTransfer = (assignment: Assignment) => {
    setSelectedAssignment(assignment);
    setShowTransferModal(true);
  };

  const handleReturnSubmit = async (returnData: any) => {
    if (!selectedAssignment) return;
    
    const response = await assignmentAPI.returnAssignment(
      selectedAssignment.assignmentId,
      returnData
    );
    
    if (response.success) {
      await loadAllData();
      setShowReturnModal(false);
      setSelectedAssignment(null);
    } else {
      throw new Error(response.error?.message || 'Failed to return item');
    }
  };

  const handleTransferComplete = async () => {
    await loadAllData();
    setShowTransferModal(false);
    setSelectedAssignment(null);
  };

  const handleExportHistory = () => {
    const csv = [
      'Date,Time,Officer,Item,Action,Performed By',
      ...filteredActivity.map(a => 
        `"${a.date}","${a.time}","${a.officer}","${a.item}","${a.action}","${a.performedBy || ''}"`
      )
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `assignment-history-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const formatLastRefresh = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins === 1) return '1 minute ago';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    return date.toLocaleTimeString();
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'Assigned': return 'bg-green-100 text-green-800';
      case 'Returned': return 'bg-blue-100 text-blue-800';
      case 'Transferred': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'Assigned': return <Package className="h-4 w-4" />;
      case 'Returned': return <RotateCcw className="h-4 w-4" />;
      case 'Transferred': return <ArrowLeftRight className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  if (error && !assignments.length) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg border border-red-200 p-8">
          <div className="flex items-center mb-4">
            <AlertTriangle className="h-6 w-6 text-red-600 mr-3" />
            <h3 className="text-xl font-bold text-red-900">Error Loading Assignments</h3>
          </div>
          <p className="text-red-700 mb-6">{error}</p>
          <button 
            onClick={loadAllData} 
            className="w-full px-4 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 mb-8">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Equipment Assignments</h1>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span className="flex items-center">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
                  Live Data
                </span>
                <span>•</span>
                <span>Updated {formatLastRefresh(lastRefresh)}</span>
              </div>
            </div>
            <button 
              onClick={loadAllData} 
              disabled={loading}
              className="inline-flex items-center px-5 py-2.5 border border-transparent text-sm font-semibold rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 shadow-md hover:shadow-lg transition-all"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 pb-8">
        {/* Compact Stats Row */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-3 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-8">
              <div className="flex items-center gap-3">
                <Package className="h-5 w-5 text-blue-600" />
                <span className="text-sm text-gray-600">Items Assigned:</span>
                <span className="text-lg font-bold text-gray-900">{totalAssigned}</span>
              </div>
              <div className="h-6 w-px bg-gray-200" />
              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 text-green-600" />
                <span className="text-sm text-gray-600">Officers Equipped:</span>
                <span className="text-lg font-bold text-gray-900">{officersWithEquipment}</span>
              </div>
            </div>
            <span className="text-xs text-gray-400">Assign equipment from Inventory page</span>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-xl shadow-md border border-gray-100 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex">
              <button
                onClick={() => setActiveTab('current')}
                className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${
                  activeTab === 'current'
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Users className="h-5 w-5 inline mr-2" />
                Current Assignments
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${
                  activeTab === 'history'
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <History className="h-5 w-5 inline mr-2" />
                Assignment History
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'current' && (
              <div className="space-y-6">
                {/* Search & Controls */}
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                  <div className="relative flex-1 max-w-md">
                    <Search className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <input
                      type="text"
                      placeholder="Search officers or equipment..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={expandAll}
                      className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      Expand All
                    </button>
                    <button
                      onClick={collapseAll}
                      className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      Collapse All
                    </button>
                  </div>
                </div>

                {/* Officer List */}
                {loading && filteredOfficers.length === 0 ? (
                  <div className="text-center py-12">
                    <RefreshCw className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
                    <p className="text-gray-600">Loading assignments...</p>
                  </div>
                ) : filteredOfficers.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Assignments Found</h3>
                    <p className="text-gray-600">
                      {searchTerm 
                        ? 'No officers or equipment match your search.'
                        : 'No equipment is currently assigned to officers.'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredOfficers.map((officer) => (
                      <div 
                        key={officer.officerId}
                        className="border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow"
                      >
                        {/* Officer Header */}
                        <button
                          onClick={() => toggleOfficerExpansion(officer.officerId)}
                          className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white hover:from-gray-100 hover:to-gray-50 transition-colors"
                        >
                          <div className="flex items-center space-x-4">
                            <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                              <User className="h-6 w-6 text-blue-600" />
                            </div>
                            <div className="text-left">
                              <h3 className="font-semibold text-gray-900">
                                {officer.officerName}
                              </h3>
                              <p className="text-sm text-gray-600">
                                Badge #{officer.badgeNumber}
                                {officer.rank && ` • ${officer.rank}`}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-4">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                              {officer.assignments.length} item{officer.assignments.length !== 1 ? 's' : ''}
                            </span>
                            {expandedOfficers.has(officer.officerId) ? (
                              <ChevronDown className="h-5 w-5 text-gray-400" />
                            ) : (
                              <ChevronRight className="h-5 w-5 text-gray-400" />
                            )}
                          </div>
                        </button>

                        {/* Officer Equipment */}
                        {expandedOfficers.has(officer.officerId) && (
                          <div className="border-t border-gray-200 bg-white">
                            <div className="divide-y divide-gray-100">
                              {officer.assignments.map((assignment) => (
                                <div 
                                  key={assignment.assignmentId}
                                  className="p-4 flex items-center justify-between hover:bg-gray-50"
                                >
                                  <div className="flex items-center space-x-4">
                                    <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center">
                                      <Package className="h-5 w-5 text-gray-600" />
                                    </div>
                                    <div>
                                      <p className="font-medium text-gray-900">
                                        {assignment.itemName}
                                      </p>
                                      <div className="flex items-center gap-3 text-sm text-gray-500">
                                        {assignment.itemSerialNumber && (
                                          <span>SN: {assignment.itemSerialNumber}</span>
                                        )}
                                        <span>•</span>
                                        <span className="flex items-center">
                                          <Calendar className="h-3 w-3 mr-1" />
                                          {new Date(assignment.assignmentDate).toLocaleDateString()}
                                        </span>
                                        <span>•</span>
                                        <span>{assignment.assignmentReason}</span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleTransfer(assignment);
                                      }}
                                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-purple-700 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
                                    >
                                      <ArrowLeftRight className="h-4 w-4 mr-1" />
                                      Transfer
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleReturn(assignment);
                                      }}
                                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                                    >
                                      <RotateCcw className="h-4 w-4 mr-1" />
                                      Return
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'history' && (
              <div className="space-y-6">
                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                  <div className="flex flex-wrap gap-3">
                    <select
                      value={dateRangeFilter}
                      onChange={(e) => setDateRangeFilter(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      <option value="7">Last 7 days</option>
                      <option value="30">Last 30 days</option>
                      <option value="90">Last 90 days</option>
                      <option value="all">All time</option>
                    </select>
                    <select
                      value={actionFilter}
                      onChange={(e) => setActionFilter(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      <option value="">All Actions</option>
                      <option value="Assigned">Assignments</option>
                      <option value="Returned">Returns</option>
                      <option value="Transferred">Transfers</option>
                    </select>
                    <div className="relative">
                      <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="Filter by officer..."
                        value={selectedOfficerFilter}
                        onChange={(e) => setSelectedOfficerFilter(e.target.value)}
                        className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>
                  </div>
                  <button
                    onClick={handleExportHistory}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </button>
                </div>

                {/* Activity List */}
                {filteredActivity.length === 0 ? (
                  <div className="text-center py-12">
                    <History className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Activity Found</h3>
                    <p className="text-gray-600">No assignment history matches your filters.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredActivity.map((activity) => (
                      <div 
                        key={activity.id}
                        className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-200 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-center space-x-4">
                          <div className={`h-10 w-10 rounded-full flex items-center justify-center ${getActionColor(activity.action)}`}>
                            {getActionIcon(activity.action)}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {activity.officer}
                            </p>
                            <p className="text-sm text-gray-600">
                              {activity.item}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionColor(activity.action)}`}>
                            {activity.action}
                          </span>
                          <p className="text-sm text-gray-500 mt-1">
                            {activity.date} at {activity.time}
                          </p>
                          {activity.performedBy && (
                            <p className="text-xs text-gray-400">
                              by {activity.performedBy}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-white rounded-lg shadow-sm border border-gray-200 text-sm text-gray-600">
            <span className="flex items-center">
              <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
              Officer-centric view
            </span>
            <span>•</span>
            <span className="flex items-center">
              <History className="h-4 w-4 text-blue-600 mr-2" />
              Full audit trail
            </span>
            <span>•</span>
            <span className="text-gray-500">
              Assign equipment from Inventory page
            </span>
          </div>
        </div>
      </div>

      {/* Return Modal */}
      <ReturnItemModal
        isOpen={showReturnModal}
        assignment={selectedAssignment}
        onClose={() => {
          setShowReturnModal(false);
          setSelectedAssignment(null);
        }}
        onReturn={handleReturnSubmit}
      />

      {/* Transfer Modal */}
      <TransferItemModal
        isOpen={showTransferModal}
        assignment={selectedAssignment}
        onClose={() => {
          setShowTransferModal(false);
          setSelectedAssignment(null);
        }}
        onSuccess={handleTransferComplete}
      />
    </div>
  );
};

export default Assignments;